import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Button, Grid, Typography, Container, 
  CircularProgress, Card, CardContent, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, useMediaQuery
} from '@mui/material';
import { 
  LocationOn, Email, Phone, VpnKey, 
  Hotel, Map
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Sidebar from '../../components/sidebars/Sidebar_eneurons';
import TopNav from '../../components/TopNav_eneurons';
import StatisticsCards from '../../components/StatisticsCard';

const generateHotelCode = () => {
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  return `enHT${randomNum}${month}${year}`;
};

const HotelRegistration = () => {
  const navigate = useNavigate();
  const [hotelCode, setHotelCode] = useState('');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  useEffect(() => {
    const generatedCode = generateHotelCode();
    setHotelCode(generatedCode);
    setFormData((prev) => ({ 
      ...prev, 
      hotelcode: generatedCode,
      password: generatedCode 
    }));
  }, []);

  const [formData, setFormData] = useState({
    hotelcode: '',
    hotel_name: '',
    hotel_title: '',
    country: 'Nigeria',
    province: '',
    address: '',
    phone: '',
    email: '',
    password: '',
    user_type: 'hotel_admin',
    sub_plan: 'standard',
    latitude: '',
    longitude: '',
    bank_name: '',
    account_number: '',
    account_name: '',
    bank_code: '',
    percentage_charge: '5.00',
    settlement_bank: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const nigerianStates = [
    "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", 
    "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", 
    "Ekiti", "Enugu", "FCT", "Gombe", "Imo", 
    "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", 
    "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", 
    "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", 
    "Yobe", "Zamfara"
  ];

  const subscriptionPlans = [
    { value: "standard", label: "Standard" },
    { value: "premium", label: "Premium"},
    { value: "enterprise", label: "Enterprise" }
  ];

  const nigerianBanks = [
    { name: "Access Bank", code: "044" },
    { name: "First Bank", code: "011" },
    { name: "Guaranty Trust Bank", code: "058" },
    { name: "Zenith Bank", code: "057" },
    { name: "United Bank for Africa", code: "033" },
    { name: "Stanbic IBTC Bank", code: "039" },
    { name: "First City Monument Bank", code: "214" },
    { name: "Union Bank", code: "032" },
    { name: "Sterling Bank", code: "232" },
    { name: "Wema Bank", code: "035" },
    { name: "Polaris Bank", code: "076" },
    { name: "Keystone Bank", code: "082" }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.hotel_name.trim()) {
      newErrors.hotel_name = 'Hotel name is required';
    }

    if (!formData.hotel_title.trim()) {
      newErrors.hotel_title = 'Hotel title is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.province) {
      newErrors.province = 'State/Province is required';
    }

    if (formData.account_number && !/^\d{10}$/.test(formData.account_number)) {
      newErrors.account_number = 'Account number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBankChange = (e) => {
    const selectedBankName = e.target.value;
    const selectedBank = nigerianBanks.find(bank => bank.name === selectedBankName);
    
    setFormData(prev => ({
      ...prev,
      bank_name: selectedBankName,
      bank_code: selectedBank?.code || '',
      settlement_bank: selectedBankName // Auto-fill settlement bank with bank name
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire({
        title: "Validation Error",
        text: "Please fix the errors in the form",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setLoading(true);

    try {
      console.log('Submitting hotel data:', formData);
      
      // Use the exact URL that works in Postman
      const apiUrl =  `${process.env.REACT_APP_API_BASE_URL}/eneurons/hotel_signup.php`;
      
      console.log('Making request to:', apiUrl);

      const response = await axios.post(apiUrl, formData, {
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        timeout: 15000,
        withCredentials: false, // Important for CORS
      });

      console.log('Hotel registration response:', response.data);

      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          text: "Hotel registered successfully!",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate('/dashboard_e-neurons');
        });
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }

    } catch (error) {
      console.error('Full error details:', error);
      
      let errorMessage = "Failed to register hotel. Please try again.";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your connection and try again.";
      } else if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // This is likely a CORS error
        console.error('No response received - CORS issue likely:', error.request);
        errorMessage = "Network error. This may be a CORS issue. Check browser console for details.";
      } else {
        errorMessage = error.message;
      }
      
      Swal.fire({
        title: "Registration Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          backgroundColor: '#f5f7fa',
          minHeight: '100vh'
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>

        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Card elevation={8} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              background: 'linear-gradient(45deg, #1976d2, #7b1fa2)',
              p: 3, 
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                Hotel Registration
              </Typography>
              <Typography variant="subtitle1">
                Register your hotel to start managing bookings and services
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  
                  {/* Hotel Code */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Hotel Code" 
                      name="hotelcode" 
                      fullWidth 
                      disabled 
                      value={hotelCode}
                      helperText="Auto-generated unique hotel code"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKey color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Subscription Plan */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Subscription Plan</InputLabel>
                      <Select
                        name="sub_plan"
                        value={formData.sub_plan}
                        onChange={handleInputChange}
                        label="Subscription Plan"
                      >
                        {subscriptionPlans.map((plan) => (
                          <MenuItem key={plan.value} value={plan.value}>
                            {plan.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Hotel Name */}
                  <Grid item xs={12}>
                    <TextField 
                      label="Hotel Name" 
                      name="hotel_name" 
                      fullWidth 
                      required
                      value={formData.hotel_name} 
                      onChange={handleInputChange}
                      error={!!errors.hotel_name}
                      helperText={errors.hotel_name}
                      placeholder="Enter your hotel name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Hotel color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Hotel Title */}
                  <Grid item xs={12}>
                    <TextField 
                      label="Hotel Title" 
                      name="hotel_title" 
                      fullWidth 
                      required
                      value={formData.hotel_title} 
                      onChange={handleInputChange}
                      error={!!errors.hotel_title}
                      helperText={errors.hotel_title}
                      placeholder="Enter your hotel title"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Hotel color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Location Information */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Country" 
                      name="country" 
                      fullWidth 
                      value={formData.country} 
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Map color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!errors.province}>
                      <InputLabel>State/Province</InputLabel>
                      <Select
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        label="State/Province"
                      >
                        {nigerianStates.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.province && (
                        <Typography variant="caption" color="error">
                          {errors.province}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  {/* Address */}
                  <Grid item xs={12}>
                    <TextField 
                      label="Full Address" 
                      name="address" 
                      fullWidth 
                      required
                      multiline
                      rows={2}
                      value={formData.address} 
                      onChange={handleInputChange}
                      error={!!errors.address}
                      helperText={errors.address}
                      placeholder="Enter complete hotel address"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Coordinates */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Latitude" 
                      name="latitude" 
                      fullWidth 
                      value={formData.latitude} 
                      onChange={handleInputChange}
                      placeholder="e.g., 6.524379"
                      type="number"
                      step="any"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Longitude" 
                      name="longitude" 
                      fullWidth 
                      value={formData.longitude} 
                      onChange={handleInputChange}
                      placeholder="e.g., 3.379206"
                      type="number"
                      step="any"
                    />
                  </Grid>
                  
                  {/* Contact Information */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Phone Number" 
                      name="phone" 
                      fullWidth 
                      required 
                      value={formData.phone} 
                      onChange={handleInputChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      type="tel"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Email Address" 
                      name="email" 
                      fullWidth 
                      required 
                      value={formData.email} 
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      type="email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Password */}
                  <Grid item xs={12}>
                    <TextField 
                      label="Password" 
                      name="password" 
                      fullWidth 
                      required 
                      type="password"
                      value={formData.password} 
                      onChange={handleInputChange}
                      error={!!errors.password}
                      helperText={errors.password || "Password must be at least 8 characters long"}
                    />
                  </Grid>

                  {/* Bank Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>
                      Bank Account Information (Optional)
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Bank Name</InputLabel>
                      <Select
                        name="bank_name"
                        value={formData.bank_name}
                        onChange={handleBankChange}
                        label="Bank Name"
                      >
                        {nigerianBanks.map((bank) => (
                          <MenuItem key={bank.name} value={bank.name}>
                            {bank.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Account Number" 
                      name="account_number" 
                      fullWidth 
                      value={formData.account_number} 
                      onChange={handleInputChange}
                      error={!!errors.account_number}
                      helperText={errors.account_number}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Account Name" 
                      name="account_name" 
                      fullWidth 
                      value={formData.account_name} 
                      onChange={handleInputChange}
                    />
                  </Grid>

                  {/* NEW BANK CODE FIELD */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Bank Code" 
                      name="bank_code" 
                      fullWidth 
                      value={formData.bank_code} 
                      onChange={handleInputChange}
                      placeholder="Auto-filled when bank is selected"
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Auto-filled based on bank selection"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Percentage Charge" 
                      name="percentage_charge" 
                      fullWidth 
                      type="number"
                      step="0.01"
                      value={formData.percentage_charge} 
                      onChange={handleInputChange}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>

                  {/* NEW SETTLEMENT BANK FIELD */}
                  <Grid item xs={12} md={6}>
                    <TextField 
                      label="Settlement Bank" 
                      name="settlement_bank" 
                      fullWidth 
                      value={formData.settlement_bank} 
                      onChange={handleInputChange}
                      placeholder="Auto-filled when bank is selected"
                      helperText="Auto-filled based on bank selection"
                    />
                  </Grid>

                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={() => navigate('/e-neurons')}
                    sx={{ 
                      minWidth: 120, 
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    type="submit" 
                    disabled={loading}
                    size="large"
                    sx={{ 
                      minWidth: 200, 
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #1976d2, #7b1fa2)'
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Register Hotel"}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default HotelRegistration;